import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Package, Check, X, Eye, Globe, EyeOff, Trash2, Pencil, Plus } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  CompactStatCard,
  DataTable,
  PageHeader,
  StandardImage,
  StatsGrid,
  useDataTable,
  type DataTableColumn,
  type DataTableFilterConfig,
} from "@/components/shared";
import { useToast } from "@/hooks/use-toast";
import type { AdminProduct } from "@/types/admin.ui";
import {
  useAdminProducts,
  useApproveProductMutation,
  useDeleteAdminProductMutation,
  useSetProductVisibilityMutation,
} from "@/hooks/queries";
import { getApiErrorMessage } from "@/services/httpClient";
import { Skeleton } from "@/components/ui/skeleton";

const statusBadge: Record<string, string> = {
  approved: "bg-success/10 text-success border-success/20",
  pending: "bg-warning/10 text-warning border-warning/20",
  rejected: "bg-destructive/10 text-destructive border-destructive/20",
  needs_revision: "bg-orange-500/10 text-orange-600 border-orange-500/20",
};

const statusFilters: DataTableFilterConfig[] = [
  {
    id: "status",
    placeholder: "Status",
    options: [
      { label: "All Status", value: "all" },
      { label: "Approved", value: "approved" },
      { label: "Pending", value: "pending" },
      { label: "Needs revision", value: "needs_revision" },
      { label: "Rejected", value: "rejected" },
      { label: "Published", value: "published" },
      { label: "Unpublished", value: "unpublished" },
    ],
  },
];

const AdminProducts = () => {
  const { data: adminProducts = [], isLoading, isError, error, refetch } = useAdminProducts();
  const approveProductMutation = useApproveProductMutation();
  const visibilityMutation = useSetProductVisibilityMutation();
  const deleteMutation = useDeleteAdminProductMutation();
  const { toast } = useToast();
  const [deleteTarget, setDeleteTarget] = useState<AdminProduct | null>(null);

  const table = useDataTable<AdminProduct>({
    data: adminProducts,
    searchKeys: ["name", "storeName", "category"],
    filterConfigs: statusFilters,
    filterPredicate: (row, state) => {
      const filter = state.filters.status;
      if (filter === "all") return true;
      if (filter === "published") return row.status === "approved" && row.isPublished;
      if (filter === "unpublished") return row.status === "approved" && !row.isPublished;
      return row.status === filter;
    },
  });

  const handleApprove = async (id: string) => {
    try {
      await approveProductMutation.mutateAsync({ id: Number(id), action: "Approve", approve: true });
      toast({ title: "Product approved and published" });
    } catch (error) {
      toast({ title: getApiErrorMessage(error), variant: "destructive" });
    }
  };

  const handlePublish = async (id: string, isActive: boolean) => {
    try {
      await visibilityMutation.mutateAsync({ id: Number(id), isActive });
      toast({ title: isActive ? "Product published" : "Product unpublished" });
    } catch (error) {
      toast({ title: getApiErrorMessage(error), variant: "destructive" });
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync(Number(deleteTarget.id));
      toast({ title: "Product deleted" });
      setDeleteTarget(null);
    } catch (error) {
      toast({ title: getApiErrorMessage(error), variant: "destructive" });
    }
  };

  const stats = useMemo(
    () => ({
      approved: adminProducts.filter((p) => p.status === "approved").length,
      pending: adminProducts.filter((p) => p.status === "pending" || p.status === "needs_revision").length,
      rejected: adminProducts.filter((p) => p.status === "rejected").length,
      published: adminProducts.filter((p) => p.status === "approved" && p.isPublished).length,
    }),
    [adminProducts]
  );

  const columns: DataTableColumn<AdminProduct>[] = [
    {
      id: "name",
      header: "Product",
      accessorKey: "name",
      sortable: true,
      mobilePriority: 1,
      cell: (product) => (
        <div className="flex items-center gap-3">
          <StandardImage src={product.image} alt={product.name} standard="product" className="h-10 w-10" />
          <div>
            <Link to={`/admin/products/${product.id}`} className="font-semibold hover:text-primary hover:underline">
              {product.name}
            </Link>
          </div>
        </div>
      ),
    },
    {
      id: "storeName",
      header: "Store",
      accessorKey: "storeName",
      sortable: true,
      mobileLabel: "Store",
      mobilePriority: 2,
      className: "text-sm",
    },
    {
      id: "category",
      header: "Category",
      accessorKey: "category",
      sortable: true,
      mobileLabel: "Category",
      mobilePriority: 3,
      className: "text-sm",
    },
    {
      id: "price",
      header: "Price",
      accessorKey: "price",
      sortable: true,
      align: "right",
      mobileLabel: "Price",
      mobilePriority: 4,
      cell: (product) => <span className="font-medium">₹{product.price}</span>,
    },
    {
      id: "stock",
      header: "Stock",
      accessorKey: "stock",
      sortable: true,
      align: "right",
      mobileLabel: "Stock",
      mobilePriority: 5,
    },
    {
      id: "status",
      header: "Status",
      accessorKey: "status",
      sortable: true,
      mobileLabel: "Status",
      mobilePriority: 6,
      cell: (product) => (
        <div className="flex flex-wrap gap-1">
          <Badge variant="outline" className={statusBadge[product.status]}>
            {product.status.replace("_", " ")}
          </Badge>
          {product.status === "approved" && (
            <Badge variant="outline" className={product.isPublished ? "bg-primary/10 text-primary border-primary/20" : "bg-muted text-muted-foreground"}>
              {product.isPublished ? "Published" : "Unpublished"}
            </Badge>
          )}
        </div>
      ),
    },
    {
      id: "submittedDate",
      header: "Submitted",
      accessorKey: "submittedDate",
      sortable: true,
      hideOnMobile: true,
      className: "text-sm text-muted-foreground",
    },
    {
      id: "actions",
      header: "Actions",
      align: "center",
      hideOnMobile: true,
      cell: (product) => (
        <div className="flex items-center justify-center gap-1">
          <Button size="icon" variant="ghost" className="h-8 w-8" asChild title="View">
            <Link to={`/admin/products/${product.id}`}><Eye className="h-4 w-4" /></Link>
          </Button>
          <Button size="icon" variant="ghost" className="h-8 w-8" asChild title="Edit">
            <Link to={`/admin/products/${product.id}/edit`}><Pencil className="h-4 w-4" /></Link>
          </Button>
          {(product.status === "pending" || product.status === "needs_revision") && (
            <Button size="icon" variant="ghost" className="h-8 w-8 text-success" onClick={() => handleApprove(product.id)}>
              <Check className="h-4 w-4" />
            </Button>
          )}
          {product.status === "approved" && !product.isPublished && (
            <Button size="icon" variant="ghost" className="h-8 w-8 text-primary" onClick={() => handlePublish(product.id, true)} title="Publish">
              <Globe className="h-4 w-4" />
            </Button>
          )}
          {product.status === "approved" && product.isPublished && (
            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => handlePublish(product.id, false)} title="Unpublish">
              <EyeOff className="h-4 w-4" />
            </Button>
          )}
          <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => setDeleteTarget(product)} title="Delete">
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Product Management"
        description="Add or edit store products with full pricing, review submissions, and publish catalog items."
        actions={
          <Button asChild>
            <Link to="/admin/products/new">
              <Plus className="mr-1.5 h-4 w-4" /> Add product
            </Link>
          </Button>
        }
      />

      <StatsGrid columns={4}>
        <CompactStatCard title="Approved" value={isLoading ? "…" : stats.approved} icon={Check} />
        <CompactStatCard title="Published" value={isLoading ? "…" : stats.published} icon={Globe} />
        <CompactStatCard title="Pending review" value={isLoading ? "…" : stats.pending} icon={Package} />
        <CompactStatCard title="Rejected" value={isLoading ? "…" : stats.rejected} icon={X} />
      </StatsGrid>

      {isLoading ? (
        <Skeleton className="h-96 rounded-xl" />
      ) : isError ? (
        <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-6 text-center space-y-3">
          <p className="text-sm text-destructive">{getApiErrorMessage(error, "Failed to load products")}</p>
          <Button variant="outline" size="sm" onClick={() => refetch()}>Retry</Button>
        </div>
      ) : (
      <DataTable
        title="All Products"
        columns={columns}
        data={table.rows}
        getRowId={(row) => row.id}
        search={table.state.search}
        onSearchChange={table.setSearch}
        searchPlaceholder="Search products..."
        filters={statusFilters}
        filterValues={table.state.filters}
        onFilterChange={table.setFilter}
        sort={table.state.sort}
        onSortChange={table.toggleSort}
        pagination={table.pagination}
        onPageChange={table.setPage}
        onPageSizeChange={table.setPageSize}
        renderMobileCard={(product) => (
          <div className="rounded-lg border border-border p-4 space-y-3">
            <div className="flex items-start gap-3">
              <StandardImage src={product.image} alt={product.name} standard="product" className="h-14 w-14 shrink-0" rounded="lg" />
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="font-semibold">{product.name}</p>
                    <p className="text-xs text-muted-foreground">{product.storeName}</p>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <Badge variant="outline" className={statusBadge[product.status]}>{product.status}</Badge>
                    {product.status === "approved" && (
                      <Badge variant="outline" className="text-xs">{product.isPublished ? "Published" : "Unpublished"}</Badge>
                    )}
                  </div>
                </div>
                <div className="mt-1 flex items-center gap-3 text-sm">
                  <span className="font-bold">₹{product.price}</span>
                  <span className="text-muted-foreground">• {product.category}</span>
                </div>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" className="flex-1 min-w-[80px]" asChild>
                <Link to={`/admin/products/${product.id}`}><Eye className="mr-1 h-3 w-3" /> View</Link>
              </Button>
              <Button size="sm" variant="outline" className="flex-1 min-w-[80px]" asChild>
                <Link to={`/admin/products/${product.id}/edit`}><Pencil className="mr-1 h-3 w-3" /> Edit</Link>
              </Button>
              {(product.status === "pending" || product.status === "needs_revision") && (
                <Button size="sm" className="flex-1 min-w-[80px]" onClick={() => handleApprove(product.id)}>
                  <Check className="mr-1 h-3 w-3" /> Approve
                </Button>
              )}
              {product.status === "approved" && (
                <Button
                  size="sm"
                  variant="outline"
                  className="flex-1 min-w-[80px]"
                  onClick={() => handlePublish(product.id, !product.isPublished)}
                >
                  {product.isPublished ? "Unpublish" : "Publish"}
                </Button>
              )}
              <Button size="sm" variant="outline" className="text-destructive" onClick={() => setDeleteTarget(product)}>
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          </div>
        )}
      />

      )}

      <AlertDialog open={Boolean(deleteTarget)} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleteTarget?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the product from the platform catalog. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminProducts;
